import { useState } from "react";
import { env } from "../lib/env";
import CopyButton from "../components/CopyButton";

interface Props {
  onBack: () => void;
  backLabel?: string;
  onContribute: () => void;
}

function ChevronIcon({ expanded }: { expanded: boolean }): JSX.Element {
  return (
    <svg
      width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ transform: expanded ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.15s" }}
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function Section({ title, defaultOpen, children }: { title: string; defaultOpen?: boolean; children: React.ReactNode }): JSX.Element {
  const [open, setOpen] = useState(defaultOpen ?? false);
  return (
    <div style={{ border: "1px solid var(--color-border)", borderRadius: "8px", overflow: "hidden" }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0.85rem 1.25rem",
          background: open ? "var(--color-bg-body)" : "var(--color-surface)",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ fontWeight: 600, fontSize: "1rem", color: "var(--color-text-primary)" }}>{title}</span>
        <ChevronIcon expanded={open} />
      </button>
      {open && (
        <div style={{ padding: "0 1.25rem 1.25rem", borderTop: "1px solid var(--color-border-light)", fontSize: "0.9rem", lineHeight: "1.7", color: "var(--color-description)" }}>
          {children}
        </div>
      )}
    </div>
  );
}

export default function HelpPage({ onBack, backLabel, onContribute }: Props): JSX.Element {
  const marketplaceName = env.REACT_APP_MARKETPLACE_NAME;
  // CLI commands (Claude Code) still register the marketplace by URL.
  // The Chat & Cowork desktop client gets it pre-installed via enterprise admin
  // tooling, so there's no GUI "add marketplace" URL to display.
  const catalogUrl = env.REACT_APP_CATALOG_GIT_URL || "";

  return (
    <div>
      <button
        onClick={onBack}
        style={{ background: "none", border: "none", color: "var(--color-brand)", cursor: "pointer", fontSize: "0.9rem", padding: 0, marginBottom: "1rem" }}
      >
        &larr; {backLabel ?? "Back to Marketplace"}
      </button>

      <h1 style={{ margin: "0 0 0.5rem 0", fontSize: "1.5rem" }}>Help &amp; Setup</h1>
      <p style={{ color: "var(--color-text-secondary)", marginBottom: "2rem", lineHeight: "1.6" }}>
        The CAI marketplace is already installed in Claude (Chat &amp; Cowork) — find plug-ins in
        Organization Plugins. Claude Code registers it via CLI.
        The same marketplace serves every surface.
      </p>

      <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>

        <Section title="Claude (Chat &amp; Cowork) — start here" defaultOpen>
          <p style={{ marginTop: "1rem" }}>
            The CAI marketplace is pre-installed for everyone via Claude&rsquo;s enterprise admin tooling —
            there&rsquo;s no marketplace to add. Plug-ins appear directly in Organization Plugins.
          </p>

          <h3 style={{ fontSize: "0.85rem", color: "var(--color-text-primary)", margin: "1.25rem 0 0.5rem" }}>Install a plug-in</h3>
          <ol style={{ margin: 0, paddingLeft: "1.25rem" }}>
            <li>In Claude, go to <strong>Customize → Organization Plugins → + Icon</strong></li>
            <li>Search for the plug-in and click <strong>+</strong> to install it — it&rsquo;s available in your next conversation</li>
          </ol>

          <h3 style={{ fontSize: "0.85rem", color: "var(--color-text-primary)", margin: "1.25rem 0 0.5rem" }}>Install an individual skill</h3>
          <p style={{ marginTop: 0 }}>
            Individual skills unpacked from a plug-in aren&rsquo;t listed in Organization Plugins — the only
            way to use one in Chat or Cowork is to download it and add it manually. (You can also download
            any plug-in this way as a fallback.)
          </p>
          <ol style={{ margin: 0, paddingLeft: "1.25rem" }}>
            <li>On a detail page, click <strong>Download</strong> to save the file</li>
            <li><strong>Plug-in:</strong> in Claude, go to <strong>Customize → Upload Plugin</strong> and select the .plugin file</li>
            <li><strong>Skill:</strong> go to <strong>Customize → Skills → Create skill → Upload a skill</strong> and select the skill.md file</li>
          </ol>

          <h3 style={{ fontSize: "0.85rem", color: "var(--color-text-primary)", margin: "1.25rem 0 0.5rem" }}>Good to know</h3>
          <p style={{ marginTop: 0 }}>
            Claude runs both skills and plug-ins. A plug-in&rsquo;s skills, slash commands, and MCP
            connectors all work in Chat — only hooks and sub-agents are Cowork-only, so a plug-in that
            ships those runs fully in Cowork.
          </p>
          <p style={{ marginTop: "0.75rem", backgroundColor: "var(--color-warning-bg)", border: "1px solid var(--color-warning-border)", borderRadius: "8px", padding: "0.6rem 0.85rem", color: "var(--color-warning-text)" }}>
            <strong>Installing is separate from runtime.</strong> A plug-in&rsquo;s bundled MCP connector routes
            through Anthropic&rsquo;s cloud in Chat and Cowork, so a connector that targets an internal-only
            server won&rsquo;t reach it there — even though the plug-in installs fine. Such connectors work in
            Claude Code, which runs locally.
          </p>

          <h3 style={{ fontSize: "0.85rem", color: "var(--color-text-primary)", margin: "1.25rem 0 0.5rem" }}>Links</h3>
          <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
            <li><a href="https://coxautoinc.sharepoint.com/sites/service-engineering-enablement/delivery-platform/SitePages/Installing-Plugins-and-Skills.aspx" target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-brand)" }} aria-label="Video: Installing plugins and skills in Claude Desktop (opens in new window)">Video: Installing plugins and skills in Claude Desktop</a></li>
            <li><a href="https://support.claude.com/en/articles/13837440-use-plugins-in-claude" target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-brand)" }}>Use plugins in Claude</a></li>
            <li><a href="https://claude.com/plugins" target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-brand)" }}>Browse plugins on claude.com</a></li>
          </ul>
        </Section>

        <Section title="Claude Code (CLI)">
          <h3 style={{ fontSize: "0.85rem", color: "var(--color-text-primary)", margin: "1rem 0 0.5rem" }}>One-time setup: add the marketplace</h3>
          <p>Register this marketplace so Claude Code knows where to find plug-ins and skills:</p>
          <CopyButton text={`claude plugin marketplace add ${catalogUrl}`} />

          <h3 style={{ fontSize: "0.85rem", color: "var(--color-text-primary)", margin: "1.25rem 0 0.5rem" }}>Installing a plug-in or skill</h3>
          <p>Copy the install command from any plug-in or skill's detail page, or run:</p>
          <p><strong>From any terminal</strong> (outside a Claude Code session):</p>
          <CopyButton text={`claude plugin install <name>@${marketplaceName}`} />
          <p>The plug-in or skill will be available next time you start Claude Code.</p>

          <p style={{ marginTop: "0.75rem" }}><strong>Inside a Claude Code session:</strong></p>
          <CopyButton text={`/plugin install <name>@${marketplaceName}`} />
          <p>
            Then run <code style={{ backgroundColor: "var(--color-surface-page)", padding: "0.1rem 0.3rem", borderRadius: "3px" }}>/reload-plugins</code> to
            activate immediately without restarting.
          </p>

          <h3 style={{ fontSize: "0.85rem", color: "var(--color-text-primary)", margin: "1.25rem 0 0.5rem" }}>Links</h3>
          <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
            <li><a href="https://code.claude.com/docs/en/discover-plugins" target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-brand)" }}>Discover and install plugins</a></li>
            <li><a href="https://code.claude.com/docs/en/plugins" target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-brand)" }}>Create plugins</a></li>
            <li><a href="https://code.claude.com/docs/en/cli-reference" target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-brand)" }}>CLI reference</a></li>
          </ul>
        </Section>

        <Section title="Contributing Plug-Ins and Skills">
          <p style={{ marginTop: "1rem" }}>
            Publishing is automatic. Any push to a <strong>public or internal</strong> (non-private)
            org-owned GitHub repo that contains a <code style={{ backgroundColor: "var(--color-surface-page)", padding: "0.1rem 0.3rem", borderRadius: "3px" }}>.claude-plugin/plugin.json</code> manifest
            is ingested by the marketplace via webhook — no extra step. Every later push keeps the
            catalog entry up to date, and removing the manifest deactivates it.
          </p>

          <h3 style={{ fontSize: "0.85rem", color: "var(--color-text-primary)", margin: "1.25rem 0 0.5rem" }}>Two ways to publish</h3>
          <ol style={{ margin: 0, paddingLeft: "1.25rem" }}>
            <li><strong>From a repo:</strong> Add a <code style={{ backgroundColor: "var(--color-surface-page)", padding: "0.1rem 0.3rem", borderRadius: "3px" }}>.claude-plugin/plugin.json</code> to any public or internal org repo — the marketplace catalogs it on the next push.</li>
            <li>
              <strong>Web form — no repo needed:</strong> Publishing a single standalone skill?{" "}
              <button
                type="button"
                onClick={onContribute}
                style={{ background: "none", border: "none", padding: 0, color: "var(--color-brand)", cursor: "pointer", font: "inherit", textDecoration: "underline" }}
              >
                Contribute a skill
              </button>{" "}
              to upload its files directly — no repository required.
            </li>
          </ol>

          <h3 style={{ fontSize: "0.85rem", color: "var(--color-text-primary)", margin: "1.25rem 0 0.5rem" }}>Links</h3>
          <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
            <li><a href="https://code.claude.com/docs/en/plugins" target="_blank" rel="noopener noreferrer" style={{ color: "var(--color-brand)" }}>Plugin authoring guide</a></li>
          </ul>
        </Section>

      </div>
    </div>
  );
}
